import { Box, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { Button, FormErrors, Select } from 'components/ui';
import {
  ErrorMessages,
  SignUpIdentificationFormData,
  signUpIdentificationFormSchema,
} from 'forms';
import { mutateProfile, useAppForm, useProfile } from 'hooks';
import React, { ReactElement, useEffect, useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { useAuthApi } from 'services/api';
import { MONTHS } from '@app/util';
import { Image } from 'components/lib';
import { Gender, USER_GENDER } from 'enum/gender.enum';
import { colorGrey } from 'customColors';

export interface IdentificationFormProps {
  onSubmit: () => void;
  goBack: () => void;
}

export const GENDERS_OPTIONS = [
  {
    label: 'Male',
    value: Gender.Male,
    icon: '/icons/gender/male.svg',
  },
  {
    label: 'Female',
    value: Gender.Female,
    icon: '/icons/gender/female.svg',
  },
  {
    label: 'Other',
    value: Gender.Other,
    icon: '/icons/gender/other.svg',
  },
];

export const IdentificationForm = ({
  onSubmit: onSubmitProp,
  goBack,
}: IdentificationFormProps): ReactElement => {
  const { profile } = useProfile();

  const {
    register,
    control,
    getValues,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, touchedFields: touched, isSubmitting },
  } = useAppForm(signUpIdentificationFormSchema, {
    mode: 'all',
  });

  const authApi = useAuthApi();

  const onSubmit = async (data: SignUpIdentificationFormData) => {
    const date = new Date(
      Date.UTC(data.year, MONTHS.indexOf(data.month), data.day),
    );
    const formData = {
      birthDay: date,
      gender: USER_GENDER[data.gender],
    };

    await authApi.confirmIdentificationDetails(formData);
    void mutateProfile();
    onSubmitProp?.();
  };

  const month = useWatch({
    name: 'month',
    control,
  });

  const dayOptions = useMemo(() => {
    const dayOptions = [<option key="">Day</option>];

    let numDays = 31;

    if (month !== undefined) {
      numDays = (MONTHS.indexOf(month) % 7) % 2 === 0 ? 31 : 30;
      if (month === 'February') numDays = 28;
    }

    for (let i = 1; i <= numDays; i++) {
      dayOptions.push(
        <option key={i} value={i}>
          {i}
        </option>,
      );
    }

    return dayOptions;
  }, [month]);

  const yearOptions = useMemo(() => {
    const yearOptions = [<option key="">Year</option>];
    for (let i = 1950; i <= new Date().getUTCFullYear(); i++) {
      yearOptions.push(
        <option key={i} value={i}>
          {i}
        </option>,
      );
    }

    return yearOptions;
  }, []);

  useEffect(() => {
    if (!profile) return;

    const birthdate = new Date(profile?.birthdate);
    const month = profile?.birthdate
      ? MONTHS[birthdate.getUTCMonth()]
      : undefined;
    const day = profile?.birthdate ? birthdate.getUTCDate() : undefined;
    const year = profile?.birthdate ? birthdate.getUTCFullYear() : undefined;
    const gender = (Gender[profile.gender] as unknown) as Gender;

    reset({
      month,
      day,
      year,
      gender,
    });
  }, [profile]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box mt={10}>
        <Box
          style={{ width: 300, maxWidth: '80vw' }}
          display="flex"
          alignItems="flex-end"
        >
          <Box width={134}>
            <Select
              label="Birthday"
              hasError={touched.month && !!errors.month}
              defaultValue={month}
              placeholder="Month"
              sx={{
                '& .input': {
                  color: ['Month', '', null, undefined].includes(
                    getValues('month'),
                  )
                    ? `${colorGrey} !important`
                    : '#23305F !important',
                  fontSize: '14px',
                },
              }}
              {...register('month')}
            >
              <option value={undefined}>Month</option>
              <>
                {MONTHS.map((month, i) => (
                  <option key={i} value={month}>
                    {month}
                  </option>
                ))}
              </>
            </Select>
          </Box>
          <Box width={85} ml={2}>
            <Select
              placeholder="Day"
              hasError={touched.day && !!errors.day}
              {...register('day', { valueAsNumber: true })}
              sx={{
                '& .input': {
                  color: [NaN].includes(getValues('day'))
                    ? `${colorGrey} !important`
                    : '#23305F !important',
                  fontSize: '14px',
                },
              }}
            >
              {dayOptions}
            </Select>
          </Box>
          <Box width={93} ml={2}>
            <Select
              placeholder="Year"
              hasError={touched.year && !!errors.year}
              {...register('year', { valueAsNumber: true })}
              sx={{
                '& .input': {
                  color: [NaN].includes(getValues('year'))
                    ? `${colorGrey} !important`
                    : '#23305F !important',
                  fontSize: '14px',
                },
              }}
            >
              {yearOptions}
            </Select>
          </Box>
        </Box>
        {touched.year && !!errors.year && (
          <FormErrors
            sx={{
              whiteSpace: 'unset',
              width: '100%',
            }}
          >
            {ErrorMessages.GradYear.INVALID}
          </FormErrors>
        )}
        {touched.month && !!errors.month && (
          <FormErrors
            sx={{
              whiteSpace: 'unset',
              width: '100%',
            }}
          >
            {ErrorMessages.Month.INVALID}
          </FormErrors>
        )}
        {touched.day && !!errors.day && (
          <FormErrors
            sx={{
              whiteSpace: 'unset',
              width: '100%',
            }}
          >
            {ErrorMessages.Day.INVALID}
          </FormErrors>
        )}
      </Box>

      <Box mt={23}>
        <Controller
          name="gender"
          control={control}
          defaultValue={profile?.gender}
          render={({ field: { ref: _ref } }) => {
            return (
              <React.Fragment>
                <FormLabel
                  sx={{
                    fontSize: 14,
                    fontWeight: 500,
                    color: theme =>
                      errors.gender
                        ? theme.palette.error.main
                        : theme.palette.text.primary,
                    cursor: 'pointer',
                    WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                    whiteSpace: 'nowrap',
                  }}
                  id="gender-label"
                >
                  Gender
                </FormLabel>
                <RadioGroup aria-labelledby="gender-label" name="gender">
                  {GENDERS_OPTIONS.map((option, index) => (
                    <Box
                      key={option.value}
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      border="1px solid #DEDEDE"
                      borderColor={
                        option.value === getValues('gender')
                          ? '#23305F'
                          : '#DEDEDE'
                      }
                      borderRadius={'10px'}
                      gap={'16px'}
                      mt={index === 0 ? 0 : '24px'}
                      sx={{
                        cursor: 'pointer',
                      }}
                      padding={'16px 24px'}
                      width={'100%'}
                      component="div"
                      onClick={() => {
                        setValue('gender', option.value);
                      }}
                    >
                      <Radio
                        sx={{
                          '& .MuiSvgIcon-root': {
                            width: '24px',
                            height: '24px',
                            color: '#23305F',
                          },
                        }}
                        value={option.value}
                        checked={option.value === getValues('gender')}
                      />
                      <Box
                        display={'flex'}
                        flexDirection={'row'}
                        alignItems={'center'}
                        gap={'10px'}
                      >
                        <Image src={option.icon} alt={option.label} />
                        <Typography
                          sx={{
                            color: '#23305F',
                            fontSize: 14,
                            fontWeight: 400,
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </RadioGroup>
              </React.Fragment>
            );
          }}
        />
        {!!errors.gender && (
          <FormErrors
            sx={{
              whiteSpace: 'unset',
              width: '100%',
            }}
          >
            {errors.gender?.message}
          </FormErrors>
        )}
      </Box>
      <Box mt={32}>
        <Button
          type="submit"
          minWidth="100%"
          variant="vibrant"
          minHeight={47}
          disabled={isSubmitting}
        >
          Next
        </Button>

        <Button
          minWidth="100%"
          sx={{
            mt: 5,
            backgroundColor: 'transparent',
            border: 'none',
            color: '#000',
            fontSize: 14,
            fontWeight: 400,
            textDecoration: 'underline',
          }}
          hoverEffect="underline"
          disabled={isSubmitting}
          onClick={goBack}
        >
          Back
        </Button>
      </Box>
    </form>
  );
};
